import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { getCountryPackages } from "@/lib/api";
import { INR, UNIT_LABEL } from "./IndiaLayout";
import BundlePricing from "@/components/sections/BundlePricing";

export default function IndiaPricing() {
  const navigate = useNavigate();
  const [pkgs, setPkgs] = useState(null);
  useEffect(() => {
    document.title = "WebsiteTech India · Pricing (₹)";
    getCountryPackages("in").then((d) => setPkgs(d.packages || [])).catch(() => setPkgs([]));
  }, []);

  const cats = pkgs ? [...new Set(pkgs.map((p) => p.category))] : [];

  return (
    <div data-testid="india-pricing">
      <section className="bg-slate-50 py-16 md:py-20 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="overline text-brand">Pricing in ₹</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 mt-2">Transparent India pricing</h1>
          <p className="text-slate-500 mt-4 md:text-lg max-w-2xl mx-auto">Independent ₹ pricing for Indian businesses — GST-ready invoicing, UPI payments. Final quotes are tailored to your project.</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {pkgs === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand" size={26} /></div>
          ) : pkgs.length === 0 ? (
            <p className="text-center text-slate-500">Pricing is being finalized. <Link to="/in/contact" className="text-brand font-semibold">Get a quote</Link>.</p>
          ) : (
            cats.map((cat) => (
              <div key={cat} className="mb-14">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">{cat}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pkgs.filter((p) => p.category === cat).map((p) => (
                    <div key={p.id} data-testid="india-package" className={`relative rounded-2xl p-6 flex flex-col ${p.popular ? "bg-gradient-to-br from-[#0B203E] to-[#071428] text-white shadow-2xl" : "bg-white border border-slate-200 card-shadow"}`}>
                      {p.popular && <span className="absolute top-4 right-4 bg-blue-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1">Popular</span>}
                      <h3 className={`font-heading text-lg font-bold ${p.popular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                      <p className={`text-sm mt-1 ${p.popular ? "text-blue-100/70" : "text-slate-400"}`}>{p.tagline}</p>
                      <div className="mt-4 flex items-end gap-1">
                        <span className={`text-xs mb-1.5 ${p.popular ? "text-blue-100/60" : "text-slate-400"}`}>{p.price_display}</span>
                        <span className={`font-heading text-3xl font-extrabold ${p.popular ? "text-white" : "text-slate-900"}`}>{INR(p.price_amount)}</span>
                        <span className={`text-sm mb-1 ${p.popular ? "text-blue-100/60" : "text-slate-400"}`}>{UNIT_LABEL[p.price_unit] || ""}</span>
                      </div>
                      <ul className="mt-5 space-y-2.5 flex-1">
                        {(p.features || []).map((f, i) => (
                          <li key={i} className={`flex items-start gap-2 text-sm ${p.popular ? "text-blue-50" : "text-slate-600"}`}>
                            <Check size={16} className={p.popular ? "text-blue-300 mt-0.5 shrink-0" : "text-brand mt-0.5 shrink-0"} /> {f}
                          </li>
                        ))}
                      </ul>
                      <Link to={`/in/contact?package=${encodeURIComponent(p.name)}`} data-testid="india-package-cta"
                        className={`mt-6 rounded-xl px-5 py-3 font-semibold text-center inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 ${p.popular ? "bg-white text-[#0B203E]" : "bg-brand text-white"}`}>
                        Get this <ArrowRight size={15} />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
          <p className="text-center text-xs text-slate-400 mt-4">Sample starting prices in ₹ (GST extra) · Final pricing depends on scope. Managed by WebsiteTech India.</p>
        </div>
      </section>
      <BundlePricing defaultCurrency="INR"
        heading="Managed growth bundles"
        subheading="Fully managed lead-generation bundles — from essentials to a complete acquisition engine. Prices per month (GST extra)."
        onSelect={(p) => navigate(`/in/contact?package=${encodeURIComponent(p.name)}`)} />
    </div>
  );
}
